"use client";

import React from 'react';
import Image from 'next/image';
import { FaSearch, FaChartBar, FaDatabase, FaCalculator, FaBook, FaFileAlt, FaDownload, FaGlobe } from 'react-icons/fa';

interface LoadingProps {
  size?: 'sm' | 'md' | 'lg';
}

const Loading: React.FC<LoadingProps> = ({ 
  size = 'md' 
}) => {
  const sizeClasses = {
    sm: 'w-24 h-24',
    md: 'w-40 h-40', 
    lg: 'w-48 h-48'
  };

  const iconSizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8'
  };

  const orbitSizeClasses = {
    sm: 'w-48 h-48',
    md: 'w-80 h-80',
    lg: 'w-96 h-96'
  };

  const icons = [
    { Icon: FaSearch, color: 'text-blue-500', delay: '0s' },
    { Icon: FaChartBar, color: 'text-green-500', delay: '0.5s' },
    { Icon: FaDatabase, color: 'text-purple-500', delay: '1s' },
    { Icon: FaCalculator, color: 'text-orange-500', delay: '1.5s' },
    { Icon: FaBook, color: 'text-red-500', delay: '2s' },
    { Icon: FaFileAlt, color: 'text-indigo-500', delay: '2.5s' },
    { Icon: FaDownload, color: 'text-pink-500', delay: '3s' },
    { Icon: FaGlobe, color: 'text-cyan-500', delay: '3.5s' }
  ];

  return (
    <div className="flex items-center justify-center w-full h-full">
      <style jsx>{`
        .slow-spin {
          animation: spin 5s linear infinite;
        }
        .slow-pulse {
          animation: pulse 2s ease-in-out infinite;
        }
        .slow-ping {
          animation: ping 2.5s cubic-bezier(0, 0, 0.2, 1) infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        @keyframes ping {
          75%, 100% { transform: scale(2); opacity: 0; }
        }
      `}</style>
      <div className="relative">
        {/* Orbiting Icons */}
        <div className={`relative ${orbitSizeClasses[size]} slow-spin`}>
          {icons.map(({ Icon, color, delay }, index) => {
            const angle = (360 / icons.length) * index;
            const radius = size === 'sm' ? 72 : size === 'md' ? 120 : 144;
            const x = Math.cos((angle * Math.PI) / 180) * radius;
            const y = Math.sin((angle * Math.PI) / 180) * radius;
            
            return (
              <div
                key={index}
                className="absolute"
                style={{
                  left: `50%`,
                  top: `50%`,
                  transform: `translate(-50%, -50%) translate(${x}px, ${y}px)`,
                  animationDelay: delay
                }}
              >
                <div className={`${iconSizeClasses[size]} ${color} animate-pulse`}>
                  <Icon />
                </div>
              </div>
            );
          })}
        </div>

        {/* Center BME Logo */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className={`${sizeClasses[size]} relative`}>
            <div className="absolute inset-0 bg-white/90 rounded-full shadow-xl slow-pulse" />
            <div className="relative z-10 flex items-center justify-center h-full">
              <Image
                src="/BME.svg"
                alt="BME Logo"
                width={size === 'sm' ? 64 : size === 'md' ? 100 : 120}
                height={size === 'sm' ? 64 : size === 'md' ? 100 : 120}
                className="brightness-100 drop-shadow-lg"
              />
            </div>
          </div>
        </div>

        {/* Pulsing Ring */}
        {/* <div className={`absolute inset-0 ${orbitSizeClasses[size]} border-2 border-bme-main/20 rounded-full slow-ping`} /> */}
        <div className={`absolute inset-0 ${orbitSizeClasses[size]} border border-bme-main/10 rounded-full slow-pulse`} />
      </div>
    </div>
  );
};

export default Loading;
